import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SidebarContext } from "../../context/SidebarContext";
import SectionMenuLeft from "../../components/SectionMenuLeft/SectionMenuLeft";
import ReactTable from "../../components/ReactTable/ReactTable";
import { config } from "../../config";
import "../Payouts/payouts.css";

const money = (amount) => `₹ ${Number(amount || 0).toFixed(2)}`;

const capitalizeFirst = (value) => {
  if (!value) return "-";

  const text = String(value);
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export default function AdminWallet() {
  const { isSidebar } = useContext(SidebarContext);
  const [wallet, setWallet] = useState({
    balance: 0,
    credits: 0,
    reversals: 0,
    settledCount: 0,
    transactions: [],
  });
  const [loading, setLoading] = useState(true);

  const fetchWallet = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${config.backEndBaseUrl}constants/admin-wallet`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        },
      );
      const result = await response.json();
      if (!response.ok || !result.status)
        throw new Error(result.message || "Unable to fetch wallet");
      setWallet(result.data);
    } catch (error) {
      console.error("Admin wallet fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "transactionId",
        header: "Transaction ID",
        Cell: ({ cell }) => (
          <span className="wallet-transaction-id">
            {cell.getValue() || "-"}
          </span>
        ),
      },

      {
        accessorKey: "bookingId",
        header: "Booking ID",
      },

      {
        accessorKey: "type",
        header: "Type",
        Cell: ({ cell }) => capitalizeFirst(cell.getValue()),
      },

      {
        accessorKey: "method",
        header: "Method",
        Cell: ({ cell }) => capitalizeFirst(cell.getValue()),
      },

      {
        accessorKey: "amount",
        header: "Amount",
        Cell: ({ cell }) => money(cell.row.original.amount),
      },

      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ cell }) => {
          const status = cell.getValue();

          return (
            <span
              className={
                String(status).toLowerCase() === "received"
                  ? "text-success"
                  : "text-danger"
              }
            >
              {capitalizeFirst(status)}
            </span>
          );
        },
      },

      {
        accessorKey: "createdAt",
        header: "Date",
        Cell: ({ cell }) =>
          cell.row.original.createdAt
            ? new Date(cell.row.original.createdAt).toLocaleString("en-IN")
            : "-",
      },
    ],
    [],
  );

  return (
    <div className="body">
      <div id="wrapper">
        <div id="page">
          <div className="layout-wrap">
            <SectionMenuLeft />
            <div className="section-content-right">
              <div
                className="main-content"
                style={{ paddingLeft: isSidebar ? "280px" : 0 }}
              >
                <div className="main-content-inner">
                  <div className="wg-box">
                    <div className="flex items-center justify-between mb-4 gap-3">
                      <div>
                        <h5 className="fare-heading">Admin Wallet</h5>
                        <p className="text-secondary mb-0">
                          Settled platform transactions from the existing
                          accounting feed.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="btn btn-outline-success"
                          onClick={fetchWallet}
                        >
                          Refresh
                        </button>
                        <Link
                          className="btn btn-dark d-flex align-items-center"
                          to="/"
                        >
                          Dashboard
                        </Link>
                      </div>
                    </div>
                    <div
                      className="dashboard-stats"
                      style={{ marginBottom: 20 }}
                    >
                      <WalletCard
                        title="Available Balance"
                        value={money(wallet.balance)}
                      />
                      <WalletCard
                        title="Total Commission"
                        value={money(wallet.totalCommission ?? wallet.credits)}
                      />
                      <WalletCard
                        title="Total Credits"
                        value={money(wallet.totalCredits ?? wallet.credits)}
                      />
                      <WalletCard
                        title="Total Debits"
                        value={money(wallet.totalDebits ?? wallet.reversals)}
                      />
                      <WalletCard
                        title="Pending Payouts"
                        value={money(wallet.pendingPayouts)}
                      />
                      <WalletCard
                        title="Completed Payouts"
                        value={money(wallet.completedPayouts)}
                      />
                    </div>
                    <ReactTable
                      columns={columns}
                      data={wallet.transactions || []}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WalletCard({ title, value }) {
  return (
    <div className="stat-card">
      <div className="stat-header">
        <h3>{title}</h3>
      </div>
      <p className="stat-value text-success">{value}</p>
    </div>
  );
}
